import { Outlet, createFileRoute } from '@tanstack/react-router'
import { Monitor, Moon, Sun } from 'lucide-react'
import type { ReactNode } from 'react'
import { LayoutDashboard } from 'lucide-react'

import logoUrl from '~/assets/logo.png'
import { useTheme, type Theme } from '~/contexts/theme-context'

/**
 * Pathless layout for `/authentication/*`. Renders the canonical two-pane
 * `.ka-auth` layout per `02-authentication.md`: form pane (left) + marketing
 * pane (right, hidden on mobile). The `<Outlet />` renders inside
 * `.ka-auth-form`, so each child route only needs to supply its heading +
 * form content — no layout concerns.
 */
export const Route = createFileRoute('/authentication')({
  component: AuthLayout,
})

const THEME_OPTIONS: ReadonlyArray<{
  readonly id: Theme
  readonly label: string
  readonly Icon: typeof Sun
}> = [
  { id: 'light', label: 'Light theme', Icon: Sun },
  { id: 'dark', label: 'Dark theme', Icon: Moon },
  { id: 'split', label: 'Split preview theme', Icon: Monitor },
]

function ThemeToggle(): ReactNode {
  const { theme, setTheme } = useTheme()

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="ka-auth-theme"
    >
      {THEME_OPTIONS.map((opt) => {
        const isActive = theme === opt.id
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={opt.label}
            onClick={() => setTheme(opt.id)}
            className={`ka-auth-theme-btn${isActive ? ' is-active' : ''}`}
          >
            <opt.Icon size={14} aria-hidden="true" />
          </button>
        )
      })}
    </div>
  )
}

function AuthLayout(): ReactNode {
  return (
    <div className="ka-auth">
      {/* Left: form pane */}
      <section className="ka-auth-form-pane">
        <div className="ka-auth-lockup">
          <div
            aria-hidden="true"
            className="ka-auth-brand-square ka-auth-brand-square--sm"
          >
            <img src={logoUrl} alt="" />
          </div>
          <div className="ka-auth-lockup-name">Kairos</div>
          <div className="ka-auth-lockup-sub">Admin</div>
        </div>

        <div className="ka-auth-body">
          <div className="ka-auth-form">
            <Outlet />
          </div>
        </div>

        <footer className="ka-auth-foot">
          <div className="ka-auth-foot-links">
            <span>© 2026 Kairos</span>
            <a href="https://thekairos.app/privacy" rel="noopener noreferrer">
              Privacy
            </a>
            <a href="https://thekairos.app/terms" rel="noopener noreferrer">
              Terms
            </a>
          </div>
          <ThemeToggle />
        </footer>
      </section>

      {/* Right: marketing pane — aria-hidden, decorative only */}
      <aside className="ka-auth-marketing" aria-hidden="true">
        <div />
        <div className="ka-auth-marketing-art">
          <LayoutDashboard size={88} strokeWidth={1.25} />
        </div>
        <div>
          <div className="ka-auth-marketing-line">
            Visibility across every workspace
          </div>
          <div className="ka-auth-marketing-helper">
            Monitor usage, manage subscriptions, and resolve support tickets
            from one place — without touching production code.
          </div>
          <div className="ka-auth-trust">
            <span className="ka-auth-trust-pill">WCAG AA</span>
            <span className="ka-auth-trust-pill">SOC 2</span>
            <span className="ka-auth-trust-pill">GDPR</span>
          </div>
        </div>
      </aside>
    </div>
  )
}
